/**
 * useKeyboardNavigation - Power user keyboard shortcuts
 * 
 * Enables Vim-style navigation through leads for fast processing.
 * 
 * Shortcuts:
 * - j/↓: Next lead
 * - k/↑: Previous lead  
 * - l/Enter: Like current lead
 * - x/Backspace: Archive current lead
 * - e: Open email composer
 * - c: Copy email to clipboard
 * - /: Focus search input
 * - Escape: Close panels/modals
 * - ?: Show keyboard shortcuts help
 */

import { useEffect, useCallback, useState } from 'react';

export interface KeyboardNavigationOptions {
  leads: any[];
  selectedIndex: number;
  onSelectIndex: (_index: number) => void;
  onLike: (_leadId: string) => void;
  onArchive: (_leadId: string) => void;
  onOpenEmail?: (_email: string) => void;
  onCopyEmail?: (_email: string) => void;
  onFocusSearch?: () => void;
  onClose?: () => void;
  enabled?: boolean;
}

export interface KeyboardShortcut {
  key: string;
  description: string;
  category: 'navigation' | 'actions' | 'general';
}

export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  { key: 'j / ↓', description: 'Next lead', category: 'navigation' },
  { key: 'k / ↑', description: 'Previous lead', category: 'navigation' },
  { key: 'l / Enter', description: 'Like lead', category: 'actions' },
  { key: 'x / Backspace', description: 'Archive lead', category: 'actions' },
  { key: 'e', description: 'Email lead', category: 'actions' },
  { key: 'c', description: 'Copy email', category: 'actions' },
  { key: '/', description: 'Focus search', category: 'general' },
  { key: 'Escape', description: 'Close panel', category: 'general' },
  { key: '?', description: 'Show shortcuts', category: 'general' },
];

export function useKeyboardNavigation({
  leads,
  selectedIndex,
  onSelectIndex,
  onLike,
  onArchive,
  onOpenEmail,
  onCopyEmail,
  onFocusSearch,
  onClose,
  enabled = true,
}: KeyboardNavigationOptions) {
  const [showHelp, setShowHelp] = useState(false);

  // Get current lead
  const currentLead = leads[selectedIndex];
  const currentLeadId = currentLead?.id || currentLead?.personId || currentLead?.lead_id;
  const currentEmail = currentLead?.data?.email || currentLead?.email || '';

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't handle if disabled or if user is typing in an input
    if (!enabled) return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      // Allow Escape to work even in inputs
      if (e.key !== 'Escape') return;
    }

    // Prevent default for our shortcuts
    const handledKeys = ['j', 'k', 'l', 'x', 'e', 'c', '/', '?', 'ArrowDown', 'ArrowUp', 'Enter', 'Backspace', 'Escape'];
    if (handledKeys.includes(e.key)) {
      // Don't prevent default for Escape - let it bubble for modal closing
      if (e.key !== 'Escape') {
        e.preventDefault();
      }
    }

    switch (e.key) {
      // Navigation
      case 'j':
      case 'ArrowDown':
        if (selectedIndex < leads.length - 1) {
          onSelectIndex(selectedIndex + 1);
        }
        break;

      case 'k':
      case 'ArrowUp':
        if (selectedIndex > 0) {
          onSelectIndex(selectedIndex - 1);
        }
        break;

      // Actions
      case 'l':
      case 'Enter':
        if (currentLeadId) {
          onLike(currentLeadId);
          // Auto-advance to next lead after liking
          if (selectedIndex < leads.length - 1) {
            setTimeout(() => onSelectIndex(selectedIndex + 1), 150);
          }
        }
        break;

      case 'x':
      case 'Backspace':
        if (currentLeadId) {
          onArchive(currentLeadId);
          // Auto-advance to next lead after archiving
          if (selectedIndex < leads.length - 1) {
            setTimeout(() => onSelectIndex(selectedIndex + 1), 150);
          }
        }
        break;

      case 'e':
        if (currentEmail && onOpenEmail) {
          onOpenEmail(currentEmail);
        }
        break;

      case 'c':
        if (currentEmail && onCopyEmail) {
          onCopyEmail(currentEmail);
        }
        break;

      // General
      case '/':
        if (onFocusSearch) {
          onFocusSearch();
        }
        break;

      case 'Escape':
        if (showHelp) {
          setShowHelp(false);
        } else if (onClose) {
          onClose();
        }
        break;

      case '?':
        setShowHelp(prev => !prev);
        break;
    }
  }, [
    enabled,
    leads,
    selectedIndex,
    currentLeadId,
    currentEmail,
    onSelectIndex,
    onLike,
    onArchive,
    onOpenEmail,
    onCopyEmail,
    onFocusSearch,
    onClose,
    showHelp,
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    showHelp,
    setShowHelp,
    shortcuts: KEYBOARD_SHORTCUTS,
  };
}

export default useKeyboardNavigation;

